import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import config from '../config';
import authService from '../services/auth';
import useTranslation from './useTranslation';
import LocalIcon from './LocalIcon';
import '../css/wall.css';
import { getOptimizedPoiImage } from '../utils/imageUtils';


// Costanti
const SKELETON_COUNT = 10;

// Genera categorie placeholder per lo skeleton
const PLACEHOLDER_CATEGORIES = [
  { id: 'p1', icon: null },
  { id: 'p2', icon: null },
  { id: 'p3', icon: null },
  { id: 'p4', icon: null }
];

// Funzione ottimizzata per calcolare la distanza
const calculateDistanceOptimized = (lat1, lon1, lat2, lon2) => {
  if (!Number.isFinite(lat1) || !Number.isFinite(lon1) || !Number.isFinite(lat2) || !Number.isFinite(lon2)) {
    return 999999; // Valore fallback per coordinate non valide
  }
  
  // Converti gradi in radianti
  const p1 = lat1 * Math.PI / 180;
  const p2 = lat2 * Math.PI / 180;
  const deltaLon = (lon2 - lon1) * Math.PI / 180;
  const deltaLat = (lat2 - lat1) * Math.PI / 180;
  
  // Formula Haversine ottimizzata
  const a = Math.sin(deltaLat/2) * Math.sin(deltaLat/2) +
          Math.cos(p1) * Math.cos(p2) * 
          Math.sin(deltaLon/2) * Math.sin(deltaLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  
  // Raggio della Terra in km
  const R = 6371;
  const distance = R * c;
  
  // Verifica che il risultato sia valido
  return Number.isFinite(distance) ? distance : 999999;
};

// Componente per i badge placeholder durante il caricamento
const SkeletonCategoryBadges = () => (
  <div className="skeleton-category-container">
    {PLACEHOLDER_CATEGORIES.map((cat, index) => (
      <div 
        key={cat.id} 
        className="skeleton-category-badge-filter"
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          backgroundColor: '#e0e0e0',
          borderRadius: '50px',
          padding: '6px 12px',
          marginRight: '8px',
          width: `${60 + (index * 15)}px`,
          height: '32px'
        }}
      />
    ))}
  </div>
);

// Componente scheletro per il caricamento - migliorato con badge
const SkeletonCard = () => (
  <div className="skeleton-card">
    <div className="card-image skeleton-image-wall">
      <div className="skeleton-img"></div>
      <div className="skeleton-category-badge"></div>
      <div className="skeleton-distance-badge" style={{
        position: 'absolute',
        bottom: '0.5rem',
        right: '0.5rem',
        width: '60px',
        height: '24px',
        background: '#e0e0e0',
        borderRadius: '50px'
      }}></div>
    </div>
  </div>
);

const Wall = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadingDistances, setLoadingDistances] = useState(true);
  const [pois, setPois] = useState([]);
  const [taxonomyData, setTaxonomyData] = useState({});
  const [selectedLang] = useState(localStorage.getItem('language') || config.languages.default);
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [error, setError] = useState(null);
  const [watchPositionId, setWatchPositionId] = useState(null);

  // Riferimenti
  const isMounted = useRef(true);
  const poisCache = useRef({});
  const taxonomyCache = useRef({});
  const timeoutRef = useRef(null);
  const debugInfo = useRef({
    startTime: Date.now(),
    steps: []
  });

  // Logging di debug
  const logDebug = (step) => {
    debugInfo.current.steps.push({
      step,
      time: Date.now() - debugInfo.current.startTime
    });
    console.log(`[${Date.now() - debugInfo.current.startTime}ms] ${step}`);
  };

  // Gestione del ciclo di vita del componente
  useEffect(() => {
    logDebug("Componente montato");
    isMounted.current = true;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    
    // Timeout di sicurezza: dopo 15 secondi, mostreremo comunque qualcosa
    timeoutRef.current = setTimeout(() => {
      if (isMounted.current && loading) {
        logDebug("Timeout di sicurezza attivato");
        // MODIFICA: Mantieni le distanze se sono già calcolate
        setLoading(false);
        setLoadingDistances(false);
        // NON modificare più pois qui, per evitare di sovrascrivere quelli con distanza
      }
    }, 15000);

    return () => {
      logDebug("Componente smontato");
      isMounted.current = false;
      document.documentElement.style.scrollBehavior = '';
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Aggiungi questa riga per pulire il watchPosition
      if (watchPositionId) {
        navigator.geolocation.clearWatch(watchPositionId);
      }
    };
  }, []);

  // Assicurati che la pagina sia sempre in cima quando il caricamento è completato
  useEffect(() => {
    if (!loading && !loadingDistances) {
      // Usa setTimeout per assicurarsi che il DOM sia completamente aggiornato
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  }, [loading, loadingDistances]);

  // Modifica l'useEffect che carica i POI per rimuovere la parte di geolocalizzazione
  useEffect(() => {
    const loadPOIs = async () => {
      try {
        logDebug("Inizio caricamento POI");
        // Ripulisci lo stato e imposta il loading
        setPois([]);
        setTaxonomyData({});
        setLoading(true);
        setLoadingDistances(true);
        setError(null);
        
        // Assicurati che l'autenticazione sia a posto
        logDebug("Verifica autenticazione");
        try {
          if (!authService.getToken()) {
            logDebug("Richiesta nuovo token");
            await authService.login();
          }
        } catch (authError) {
          logDebug("Errore autenticazione: " + authError.message);
          // Continua comunque, la richiesta API gestirà l'errore
        }
        
        // Carica i POI
        logDebug("Richiesta API POI");
        const response = await authService.get(config.API_ENDPOINTS.getPOIList(selectedLang));
        
        if (!isMounted.current) {
          logDebug("Componente non più montato dopo API POI");
          return;
        }
        
        if (!response || !Array.isArray(response)) {
          logDebug("Risposta API non valida");
          throw new Error("Risposta API non valida");
        }
        
        logDebug(`Ricevuti ${response.length} POI`);
        // Salva in cache e aggiorna la UI con POI non ordinati
        poisCache.current[selectedLang] = response;
        setPois(response);
        
        // AGGIUNGI QUI - sblocca subito l'interfaccia dopo aver ricevuto i POI
        setLoading(false);

        // IMPORTANTE: Salva il riferimento al timeout in una variabile che puoi cancellare
        let localTimeoutId = null;

        logDebug("Avvio geolocalizzazione");
        if (navigator.geolocation) {
          try {
            // Cancella eventuali watch precedenti
            if (watchPositionId) {
              navigator.geolocation.clearWatch(watchPositionId);
            }
            
            // Imposta il timeout di sicurezza locale - ridotto a 2 secondi per migliore UX
            localTimeoutId = setTimeout(() => {
              if (isMounted.current && loadingDistances) {
                logDebug("Timeout locale geolocalizzazione");
                // Fallback se dopo 2 secondi non abbiamo ancora posizione
                const sortedAlphabetically = [...response].sort((a, b) => {
                  return a.title[0].value.localeCompare(b.title[0].value);
                });
                setPois(sortedAlphabetically);
                setLoadingDistances(false);
              }
            }, 2000);

            const id = navigator.geolocation.watchPosition(
              (position) => {
                if (!isMounted.current) return;
                
                // IMPORTANTE: Cancella il timeout locale quando la geolocalizzazione ha successo
                if (localTimeoutId) {
                  clearTimeout(localTimeoutId);
                  localTimeoutId = null;
                }
                
                logDebug("Geolocalizzazione completata");
                
                const userPos = {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude
                };
                
                // Calcola le distanze e ordina
                const poisWithDistance = response.map(poi => {
                  let distance = null;
                  if (poi && poi.field_geolocalizzazione && poi.field_geolocalizzazione[0]) {
                    const { lat, lng } = poi.field_geolocalizzazione[0];
                    distance = calculateDistanceOptimized(
                      parseFloat(userPos.lat), 
                      parseFloat(userPos.lng), 
                      parseFloat(lat), 
                      parseFloat(lng)
                    );
                  }
                  return { ...poi, distance };
                });
                
                // Ordina per distanza
                const sortedPois = poisWithDistance.sort((a, b) => {
                  if (a.distance === null) return 1;
                  if (b.distance === null) return -1;
                  return a.distance - b.distance;
                });
                
                if (isMounted.current) {
                  logDebug("Aggiornamento UI con distanze");
                  setPois(sortedPois);
                  setLoading(false);
                  setLoadingDistances(false);
                  
                  // Rimuovi il watch dopo il primo aggiornamento riuscito
                  navigator.geolocation.clearWatch(id);
                  setWatchPositionId(null);
                }
              },
              (error) => {
                // IMPORTANTE: Cancella il timeout locale anche in caso di errore
                if (localTimeoutId) {
                  clearTimeout(localTimeoutId);
                  localTimeoutId = null;
                }
                
                if (!isMounted.current) return;
                logDebug("Errore geolocalizzazione: " + error.message);
                
                // Fallback: ordina i POI alfabeticamente
                const sortedAlphabetically = [...response].sort((a, b) => {
                  return a.title[0].value.localeCompare(b.title[0].value);
                });
                setPois(sortedAlphabetically);
                setLoading(false);
                setLoadingDistances(false);
                
                // Rimuovi il watch in caso di errore
                navigator.geolocation.clearWatch(id);
                setWatchPositionId(null);
              },
              { 
                enableHighAccuracy: true, // Usa alta precisione come in maplist
                timeout: 3000, // Ridotto da 10s a 3s per migliore responsiveness
                maximumAge: 30000 // Usa stesso valore di maplist
              }
            );
            
            setWatchPositionId(id);
          } catch (geoError) {
            // IMPORTANTE: Cancella il timeout locale anche in caso di eccezione
            if (localTimeoutId) {
              clearTimeout(localTimeoutId);
              localTimeoutId = null;
            }
            
            // Resto del codice esistente...
          }
        } else {
          // E qui
          setLoadingDistances(false);
          // ... resto del codice esistente...
        }
      } catch (error) {
        if (!isMounted.current) return;
        logDebug("Errore generale: " + error.message);
        console.error("Errore nel caricamento:", error);
        setLoading(false);
        setLoadingDistances(false);
        setError("Errore di caricamento: " + error.message);
      }
    };
    
    loadPOIs();
  }, [selectedLang]);

  // Modifica l'useEffect che carica le tassonomie con caricamento batch ottimizzato
  useEffect(() => {
    const loadTaxonomies = async () => {
      if (!pois || pois.length === 0) return;
      
      try {
        logDebug("Inizio caricamento tassonomie");
        const taxonomy = { ...taxonomyData };
        const taxonomiesToFetch = [];
        
        // Raccogli tutte le tassonomie da caricare
        for (const poi of pois) {
          if (poi && poi.field_tipologia_poi && poi.field_tipologia_poi.length > 0) {
            const taxonomyId = poi.field_tipologia_poi[0].target_id;
            if (!taxonomyId) continue;
            
            if (taxonomy[taxonomyId] || taxonomyCache.current[taxonomyId]) {
              if (taxonomyCache.current[taxonomyId] && !taxonomy[taxonomyId]) {
                taxonomy[taxonomyId] = taxonomyCache.current[taxonomyId];
              }
              continue;
            }
            
            if (!taxonomiesToFetch.includes(taxonomyId)) {
              taxonomiesToFetch.push(taxonomyId);
            }
          }
        }
        
        if (taxonomiesToFetch.length === 0) {
          if (Object.keys(taxonomy).length !== Object.keys(taxonomyData).length) {
            setTaxonomyData(taxonomy);
          }
          return;
        }
        
        logDebug(`Caricamento batch di ${taxonomiesToFetch.length} tassonomie`);
        
        // Caricamento parallelo ottimizzato - tutte in una volta ma con gestione errori individuali
        const taxonomyPromises = taxonomiesToFetch.map(async (taxonomyId) => {
          try {
            const response = await authService.get(config.API_ENDPOINTS.getTaxonomyTerm(taxonomyId, selectedLang));
            if (!isMounted.current || !response) return null;
            
            const taxData = {
              id: taxonomyId,
              title: response.name?.[0]?.value || 'Categoria',
              icon: response.field_icona?.[0]?.url || null
            };
            
            taxonomy[taxonomyId] = taxData;
            taxonomyCache.current[taxonomyId] = taxData;
            return taxData;
          } catch (error) {
            console.error(`Errore caricamento tassonomia ${taxonomyId}:`, error);
            return null;
          }
        });
        
        // Attendi tutti i caricamenti in parallelo
        await Promise.allSettled(taxonomyPromises);
        
        if (isMounted.current) {
          logDebug("Aggiornamento tassonomie completato");
          setTaxonomyData(taxonomy);
        }
      } catch (error) {
        logDebug("Errore caricamento tassonomie: " + error.message);
        console.error("Errore nel caricamento delle tassonomie:", error);
      }
    };
    
    loadTaxonomies();
  }, [pois, selectedLang]);

  // Filtra i POI in base alle categorie selezionate (selezione multipla)
  const filteredPois = useMemo(() => {
    if (!pois || selectedCategories.size === 0) return pois || [];
    
    return pois.filter(poi => 
      poi && poi.field_tipologia_poi && 
      poi.field_tipologia_poi.length > 0 && 
      selectedCategories.has(poi.field_tipologia_poi[0].target_id)
    );
  }, [pois, selectedCategories]);
  
  // Organizza i POI in due colonne alternate - rendering di tutti i POI filtrati
  const columns = useMemo(() => {
    const col1 = [];
    const col2 = [];
    
    if (filteredPois && filteredPois.length > 0) {
      // MODIFICA: usa tutti i filteredPois invece di limitare a itemsToRender
      filteredPois.forEach((poi, i) => {
        if (i % 2 === 0) {
          col1.push(poi);
        } else {
          col2.push(poi);
        }
      });
    }
    
    return [col1, col2];
  }, [filteredPois]); // MODIFICA: rimossa dipendenza da itemsToRender
  
  // Estrai le categorie uniche per i filtri in alto - ordinate alfabeticamente
  const uniqueCategories = useMemo(() => {
    const categoriesMap = new Map();
    
    if (!pois || !Array.isArray(pois) || !taxonomyData) return [];
    
    for (const poi of pois) {
      if (poi && poi.field_tipologia_poi && poi.field_tipologia_poi.length > 0) {
        const taxonomyId = poi.field_tipologia_poi[0].target_id;
        if (taxonomyData[taxonomyId] && !categoriesMap.has(taxonomyId)) {
          categoriesMap.set(taxonomyId, {
            id: taxonomyId,
            title: taxonomyData[taxonomyId].title || 'Categoria',
            icon: taxonomyData[taxonomyId].icon || null
          });
        }
      }
    }
    
    // Converti in array e ordina alfabeticamente
    return Array.from(categoriesMap.values())
      .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }, [pois, taxonomyData]);
  
  // Toggle del filtro categoria - selezione multipla
  const handleCategoryFilter = (categoryId) => {
    setSelectedCategories(prevCategories => {
      const newCategories = new Set(prevCategories);
      if (newCategories.has(categoryId)) {
        newCategories.delete(categoryId);
      } else {
        newCategories.add(categoryId);
      }
      return newCategories;
    });
  };
  
  // Formatta la distanza sempre in km
  const formatDistance = (meters) => {
    if (meters === null || meters === undefined || !isFinite(meters)) return t('distanzaNonDisponibile');
    return `${meters.toFixed(1)} km`;
  };
  
  // Determina se mostrare skeleton o contenuto
  const isLoading = loading || loadingDistances;

  // Controlla se ci sono POI da mostrare
  const hasPoisToShow = !isLoading && filteredPois && filteredPois.length > 0;
  const hasNoPoisToShow = !isLoading && (!filteredPois || filteredPois.length === 0);

  const renderCategoryIcon = (poiData) => {
    if (!poiData || !poiData.field_tipologia_poi || !poiData.field_tipologia_poi.length) {
      return null;
    }
    
    const taxonomyId = poiData.field_tipologia_poi[0].target_id;
    const taxData = taxonomyData[taxonomyId];
    
    console.log("Dati tassonomia per icona:", taxData);
    
    if (!taxData) return null;
    
    // Se c'è un'icona, usala, altrimenti mostra solo il titolo
    return (
      <div className="category-badge">
        {taxData.icon ? (
          <img src={taxData.icon} alt={taxData.title} className="category-icon" />
        ) : (
          <span>{taxData.title}</span>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Filtri delle categorie scrollabili orizzontalmente */}
      {!isLoading && uniqueCategories.length > 0 && (
        <div className="category-filter-scroll">
          <div className="category-filter-container">
            {uniqueCategories.map(category => (
              <div
                key={category.id}
                className={`category-badge-filter ${selectedCategories.has(category.id) ? 'selected' : ''}`}
                onClick={() => handleCategoryFilter(category.id)}
              >
                {category.icon && <img src={category.icon} alt={category.title} />}
                <span>{category.title}</span>
                {selectedCategories.has(category.id) && (
                  <div className="category-check">
                    <svg 
                      className="material-symbols-outlined" 
                      viewBox="0 -960 960 960" 
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ display: 'inline-block', verticalAlign: 'middle' }}
                    >
                      <path d="M400-304 240-464l56-56 104 104 264-264 56 56-320 320Z" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Card dei POI o Skeleton durante il caricamento */}
      {isLoading ? (
        <div className="wall-container">
          <div className="wall-column">
            {Array.from({ length: Math.ceil(SKELETON_COUNT / 2) }).map((_, index) => (
              <SkeletonCard key={`skeleton-1-${index}`} />
            ))}
          </div>
          <div className="wall-column">
            {Array.from({ length: Math.floor(SKELETON_COUNT / 2) }).map((_, index) => (
              <SkeletonCard key={`skeleton-2-${index}`} />
            ))}
          </div>
        </div>
      ) : hasNoPoisToShow ? (
        <div className="alert alert-info m-3">
          {selectedCategories.size > 0 ? t('nessunRisultatoFiltro') : t('nessunRisultatoDisponibile')}
        </div>
      ) : (
        <div className="wall-container">
          <div className="wall-column">
            {columns[0] && columns[0].map((poi) => (
              <Link 
                key={poi.nid[0].value}
                to={`/poi/${poi.nid[0].value}`}
                state={{ selectedPOI: poi, taxonomyData }}
                style={{ textDecoration: 'none' }}
              >
                <div className="poi-card">
                  <div className="card-image">
                    <img 
                      src={getOptimizedPoiImage(poi, 'max_650x650', config.assets?.logo)}
                      alt={poi.title[0].value}
                      loading="lazy"
                      decoding="async"
                    />
                    
                    {/* Badge della categoria (solo icona) */}
                    {renderCategoryIcon(poi)}
                    
                    {/* Badge della distanza */}
                    {poi.distance !== null && (
                      <div className="distance-badge">
                        <LocalIcon icon="near_me" />
                        {formatDistance(poi.distance)}
                      </div>
                    )}
                  </div>
                  <div className="card-info">
                    <h5>{poi.title[0].value}</h5>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="wall-column">
            {columns[1] && columns[1].map((poi) => (
              <Link 
                key={poi.nid[0].value}
                to={`/poi/${poi.nid[0].value}`}
                state={{ selectedPOI: poi, taxonomyData }}
                style={{ textDecoration: 'none' }}
              >
                <div className="poi-card">
                  <div className="card-image">
                    <img 
                      src={getOptimizedPoiImage(poi, 'max_650x650', `${process.env.PUBLIC_URL}/assets/${config.brandFolder}/placeholder.jpg`)} 
                      alt={poi.title[0].value}
                      loading="lazy"
                      decoding="async"
                    />
                    
                    {/* Badge della categoria (solo icona) */}
                    {renderCategoryIcon(poi)}
                    
                    {/* Badge della distanza */}
                    {poi.distance !== null && (
                      <div className="distance-badge">
                        <LocalIcon icon="near_me" />
                        {formatDistance(poi.distance)}
                      </div>
                    )}
                  </div>
                  <div className="card-info">
                    <h5>{poi.title[0].value}</h5>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Wall; 
